import type { Map as MlMap } from 'maplibre-gl';
import { safeHex, shade, textOn } from '@/lib/colors';

/**
 * Map icons drawn on a canvas: buses (top view in the line colour), line-number badges and the bus
 * stop sign. Images are added to a map on first use (styleimagemissing), and the same drawings are
 * available as data URLs for DOM markers.
 */
const RATIO = 2; // drawn at 2× for sharp icons on high-density screens
export const INACTIVE = '#94a3b8';

function canvas(w: number, h: number) {
  const el = document.createElement('canvas');
  el.width = w;
  el.height = h;
  const ctx = el.getContext('2d')!;
  ctx.scale(RATIO, RATIO);
  return ctx;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
}

/**
 * A bus seen from above, nose up (heading 0 = north), in the line colour: body with a darker outline,
 * tinted windscreen at the front, rear window, roof hatches and side mirrors.
 */
export function drawBus(color: string): ImageData {
  const [w, h] = [26 * RATIO, 52 * RATIO];
  const ctx = canvas(w, h);
  const body = safeHex(color);
  const edge = shade(body, 0.4);
  // soft shadow
  ctx.fillStyle = 'rgba(15, 23, 42, 0.25)';
  roundRect(ctx, 4.5, 5.5, 18, 44, 5);
  ctx.fill();
  // mirrors
  ctx.fillStyle = edge;
  ctx.fillRect(1.5, 8, 3, 2);
  ctx.fillRect(21.5, 8, 3, 2);
  // body
  ctx.fillStyle = body;
  ctx.strokeStyle = edge;
  ctx.lineWidth = 1.5;
  roundRect(ctx, 4, 3, 18, 45, 5);
  ctx.fill();
  ctx.stroke();
  // windscreen (front) and rear window
  ctx.fillStyle = 'rgba(15, 23, 42, 0.78)';
  roundRect(ctx, 6, 5.5, 14, 6.5, 2.5);
  ctx.fill();
  roundRect(ctx, 7, 43.5, 12, 2.5, 1);
  ctx.fill();
  // roof: light panel with two hatches
  ctx.fillStyle = 'rgba(255, 255, 255, 0.28)';
  roundRect(ctx, 7.5, 14.5, 11, 27, 2);
  ctx.fill();
  ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
  ctx.fillRect(10, 19, 6, 4);
  ctx.fillRect(10, 32, 6, 4);
  return ctx.getImageData(0, 0, w, h);
}

/** Round badge with the line number, always upright. */
function drawBadge(color: string, label: string): ImageData {
  const text = label.slice(0, 4);
  const size = 22;
  const width = Math.max(size, 10 + text.length * 7);
  const [w, h] = [(width + 4) * RATIO, (size + 4) * RATIO];
  const ctx = canvas(w, h);
  const fill = safeHex(color);
  ctx.fillStyle = fill;
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 2;
  roundRect(ctx, 2, 2, width, size, size / 2);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = textOn(fill);
  ctx.font = '700 12px Inter, ui-sans-serif, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, 2 + width / 2, 2 + size / 2 + 0.5);
  return ctx.getImageData(0, 0, w, h);
}

/** Icon ids encode what to draw, so any colour/label is generated on first use. */
export const busIcon = (color: string) => `bus:${safeHex(color)}`;
export const badgeIcon = (color: string, label: string) => `badge:${safeHex(color)}:${label}`;

const registered = new WeakSet<MlMap>();
export function registerIcons(map: MlMap) {
  if (registered.has(map)) return;
  registered.add(map);
  map.on('styleimagemissing', ({ id }: { id: string }) => {
    if (map.hasImage(id)) return;
    if (id === WARN_ICON) map.addImage(id, drawWarning(), { pixelRatio: RATIO });
    else if (id === STATION_ICON) map.addImage(id, drawStation(), { pixelRatio: RATIO });
    else if (id === STATION_ICON_SELECTED) map.addImage(id, drawStation(true), { pixelRatio: RATIO });
    else if (id.startsWith('bus:')) map.addImage(id, drawBus(id.slice(4)), { pixelRatio: RATIO });
    else if (id.startsWith('arrow:')) map.addImage(id, drawArrow(id.slice(6)), { pixelRatio: RATIO });
    else if (id.startsWith('badge:')) {
      const [, color, ...label] = id.split(':');
      map.addImage(id, drawBadge(color!, label.join(':')), { pixelRatio: RATIO });
    }
  });
}

export const STATION_ICON = 'station';
/** Size of the station sign by zoom (the same on every map). */
export const STATION_SIGN_SIZE = [
  'interpolate',
  ['linear'],
  ['zoom'],
  10,
  0.45,
  12,
  0.55,
  15,
  0.85,
  18,
  1.1,
] as never;
export const STATION_ICON_SELECTED = 'station-selected';

/** Bus stop sign: blue rounded square, white border, white bus front. */
export function drawStation(selected = false): ImageData {
  const s = selected ? 30 : 22;
  const [w, h] = [(s + 4) * RATIO, (s + 4) * RATIO];
  const ctx = canvas(w, h);
  const k = s / 22;
  ctx.fillStyle = 'rgba(15, 23, 42, 0.25)';
  roundRect(ctx, 2.5, 3.5, s, s, 5 * k);
  ctx.fill();
  ctx.fillStyle = selected ? '#f5b700' : '#0b3f82';
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 2;
  roundRect(ctx, 2, 2, s, s, 5 * k);
  ctx.fill();
  ctx.stroke();
  // bus front
  const x = 2 + 5 * k;
  const y = 2 + 4.5 * k;
  ctx.fillStyle = '#ffffff';
  roundRect(ctx, x, y, 12 * k, 11 * k, 2.5 * k);
  ctx.fill();
  ctx.fillStyle = selected ? '#f5b700' : '#0b3f82';
  roundRect(ctx, x + 1.6 * k, y + 1.8 * k, 8.8 * k, 4.6 * k, 1 * k);
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(x + 1.2 * k, y + 11 * k, 2.4 * k, 2 * k);
  ctx.fillRect(x + 8.4 * k, y + 11 * k, 2.4 * k, 2 * k);
  ctx.fillStyle = selected ? '#f5b700' : '#0b3f82';
  ctx.beginPath();
  ctx.arc(x + 2.6 * k, y + 8.6 * k, 1.1 * k, 0, Math.PI * 2);
  ctx.arc(x + 9.4 * k, y + 8.6 * k, 1.1 * k, 0, Math.PI * 2);
  ctx.fill();
  return ctx.getImageData(0, 0, w, h);
}

/** The same drawings as data URLs (for DOM markers, e.g. the draggable station in its editor). */
export function iconUrl(image: ImageData): string {
  const el = document.createElement('canvas');
  el.width = image.width;
  el.height = image.height;
  el.getContext('2d')!.putImageData(image, 0, 0);
  return el.toDataURL();
}

/** Direction chevron for lines (points right; the map rotates it along the line). */
function drawArrow(color: string): ImageData {
  const [w, h] = [16 * RATIO, 16 * RATIO];
  const ctx = canvas(w, h);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(5, 3.5);
  ctx.lineTo(11, 8);
  ctx.lineTo(5, 12.5);
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 4.5;
  ctx.stroke();
  ctx.strokeStyle = safeHex(color);
  ctx.lineWidth = 2.2;
  ctx.stroke();
  return ctx.getImageData(0, 0, w, h);
}
export const arrowIcon = (color: string) => `arrow:${safeHex(color)}`;

export const WARN_ICON = 'warn';

/** Small amber warning badge (a bus that may be on the wrong line). */
function drawWarning(): ImageData {
  const s = 18;
  const [w, h] = [(s + 4) * RATIO, (s + 4) * RATIO];
  const ctx = canvas(w, h);
  ctx.fillStyle = '#f59e0b';
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(2 + s / 2, 2 + s / 2, s / 2, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(2 + s / 2 - 1.2, 6, 2.4, 6.5);
  ctx.beginPath();
  ctx.arc(2 + s / 2, 15, 1.4, 0, Math.PI * 2);
  ctx.fill();
  return ctx.getImageData(0, 0, w, h);
}
